import { supabase, getActiveSchema } from '../../../../supabaseClient';
import { calculateProgramSchedule } from '@/shared/logic/program-logic';

/**
 * PROGRAM REPOSITORY
 * Specialized IO for training programs, templates, and scheduling.
 */

const getClient = () => {
    const schema = getActiveSchema();
    return supabase.schema(schema);
};

const PROGRAM_COLUMNS = 'id, user_id, name, cycle_length, archived_at, created_at, updated_at';
const PROGRAM_DAY_COLUMNS = 'id, program_id, label, sequence_number, created_at, updated_at';

export const ProgramRepo = {
    async fetchPrograms(showArchived = false) {
        let query = getClient().from('programs').select(PROGRAM_COLUMNS);
        if (!showArchived) query = query.is('archived_at', null);
        return await query.order('created_at', { ascending: false });
    },

    async upsertProgram(payload: any) {
        return await getClient().from('programs').upsert([payload]).select(PROGRAM_COLUMNS).maybeSingle();
    },

    async setProgramArchivedStatus(programId: string, timestamp: string | null) {
        return await getClient().from('programs').update({ archived_at: timestamp }).eq('id', programId);
    },

    async fetchProgramDays(programId: string) {
        return await getClient()
            .from('program_days')
            .select(PROGRAM_DAY_COLUMNS)
            .eq('program_id', programId)
            .order('sequence_number');
    },

    async fetchDeepProgram(programId: string) {
        return await getClient()
            .from('program_days')
            .select(`
                id, label, sequence_number,
                sessions (
                    id, name,
                    blocks (
                        id, label, block_type, sort_order,
                        block_items (
                            id, exercise_library_id, target_sets, target_reps, target_weight, target_rpe, tempo, set_targets, metric_type, sort_order,
                            exercise_library ( name, technique_cues )
                        )
                    )
                )
            `)
            .neq('sessions.blocks.block_type', 'HISTORY')
            .neq('sessions.blocks.block_type', 'ARCHIVED')
            .eq('program_id', programId)
            .order('sequence_number');
    },

    async scheduleProgram(programId: string, startDateString: string, numCycles = 1, clearExisting = false) {
        try {
            const client = getClient();

            // 1. Fetch Program Structure
            const { data: days, error: daysError } = await client
                .from('program_days')
                .select('id, sequence_number, label, sessions(id)')
                .eq('program_id', programId)
                .order('sequence_number', { ascending: true });

            if (daysError) throw daysError;
            if (!days || days.length === 0) throw new Error("Program has no days to schedule.");

            // 2. Auth Context
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error("User not authenticated.");

            // 3. Logic: Calculate planned events (Stateless)
            const eventsToInsert = calculateProgramSchedule({
                programId,
                userId: user.id,
                days,
                startDate: startDateString,
                numCycles
            });

            if (eventsToInsert.length === 0) return { success: true, count: 0 };

            // 4. Clean overlapping plans (Optional)
            if (clearExisting) {
                const lastDate = eventsToInsert[eventsToInsert.length - 1].start_time;
                await client
                    .from('events')
                    .delete()
                    .gte('start_time', startDateString + 'T00:00:00')
                    .lte('start_time', lastDate)
                    .eq('status', 'PLANNED')
                    .eq('type', 'PERFORMANCE'); 
            }

            // 5. Bulk Insert
            const { error: insertError } = await client
                .from('events')
                .insert(eventsToInsert);

            if (insertError) throw insertError;

            return { success: true, count: eventsToInsert.length };

        } catch (err: any) {
            console.error("Schedule Program Error:", err);
            throw err;
        }
    }
};
